import { applyOptimisticPin } from '@/lib/pinnedSlotReorder'
import type { Post, PostsPage } from '@/types/posts'

/**
 * Cross-mount coordination for a pin/unpin that is in flight, or that has been
 * written to the PDS and is waiting for the AppView to index it.
 *
 * Two distinct problems live here, keyed the same way (the JSON-stringified
 * pinned-slot query key, so the state is shared by every mount that looks at
 * that one cache entry):
 *
 * 1. **Epoch.** A generation counter per query key, so an orphaned
 *    commit-confirmation chain from a since-unmounted `usePostEngagement` can
 *    never win against a newer chain for the same key. A mount-scoped `useRef`
 *    cannot do this: the confirm poll runs ~56s worst case (see
 *    `pinnedPostCommit.ts`), and a profile navigated away from and back resets
 *    a ref while the old chain is still in flight.
 *
 * 2. **The reconcile window.** Between the optimistic write and the AppView
 *    confirming it, the *server* still answers with pre-pin data. The confirm
 *    chain is careful about its own write, but it is not the only thing that
 *    puts server data into that cache entry. A reconnect refetch, a
 *    pull-to-refresh, a remount (this feed runs `staleTime: 0` — see
 *    `isOwnPinnedSlotFeed` in `usePosts.ts`), or any `invalidateQueries` on the
 *    `['posts', identifier]` prefix from elsewhere in the app (`removePost` and
 *    every engagement mutation's error path do exactly that) will each fetch
 *    and land a page that predates the pin, silently reverting a pin the user
 *    has already been told succeeded, with no error surfaced anywhere.
 *
 *    So while an operation is pending, `usePosts` routes every result for that
 *    key — refetch, next page, or `setQueryData` — through
 *    `reconcilePendingPin`, which re-applies the user's own pin/unpin on top of
 *    whatever the server said instead of letting it replace the state
 *    wholesale. The window is opened in `onMutate` and closed once the
 *    operation reaches a terminal state (confirmed, timed out, failed,
 *    discarded); it is deliberately never left open indefinitely, because past
 *    that point server truth should win again. The one exception is the
 *    confirmed case, which closes after a short grace period — see
 *    `scheduleEndPinOperation`.
 *
 * The key is whichever cache entry the write actually targets, not a fixed
 * AT-protocol shape: for AT that is the pinned-slot feed, for Mastodon it is
 * the tab the action was taken from (Mastodon serialises `pinned` on replies
 * too, so Pin/Unpin is live on a `collection`/`POSTS_WITH_REPLIES` entry). An
 * earlier revision hardcoded the AT shape here, which made every one of these
 * calls inert for Mastodon — the bookkeeping ran, but no query ever read it.
 */

/** A pin/unpin whose result the server has not caught up with yet. */
export interface PendingPinOperation {
    /** The post the action was taken on. */
    post: Post
    /** True when pinning, false when unpinning. */
    next: boolean
    /** The epoch this operation was opened under. */
    epoch: number
    /**
     * The account the action was taken under (the Bluesky session DID, or
     * undefined when there is no Bluesky session).
     *
     * The query key carries `identifier` and `hasSession`, but not *which*
     * account is signed in — so after an account switch, account B viewing
     * A's profile resolves to the very same cache entry A pinned from. Without
     * this, A's pending pin re-applies itself over every server response B
     * gets, hoisting a post into a pinned slot on someone else's view. The
     * confirmed-page write in `usePostEngagement` already guards this way; the
     * window has to as well.
     */
    initiatingDid: string | undefined
}

const epochByKey = new Map<string, number>()
const pendingByKey = new Map<string, PendingPinOperation>()
const graceTimerByKey = new Map<string, ReturnType<typeof setTimeout>>()

function clearGraceTimer(key: string): void {
    const timer = graceTimerByKey.get(key)
    if (timer !== undefined) {
        clearTimeout(timer)
        graceTimerByKey.delete(key)
    }
}

/**
 * How long the window stays open *after* the AppView has confirmed the pin.
 *
 * Closing on confirmation alone leaves one race uncovered: a fetch of this same
 * key that started *before* the confirm landed (an unrelated engagement
 * mutation's `onError` invalidating the `['posts', identifier]` prefix, a
 * reconnect, a remount) carries genuinely pre-pin bytes and can resolve *after*
 * the window closed, overwriting the confirmed page with nothing left to repair
 * it. `onMutate`'s `cancelQueries` cannot help — it only cancels what was in
 * flight at click time.
 *
 * A fetch that *starts* after confirmation is not a problem: the own pinned-slot
 * feed reads page one through a cache-bypassing agent (`isOwnPinnedSlotFeed` in
 * `usePosts.ts`), so it is an origin read of an AppView that has already been
 * proven to show the pin. Only the straggler needs covering, so this is a few
 * seconds, not another long window — long enough to outlast an in-flight
 * request, short enough that a pin made from another device in the same breath
 * is not masked for meaningfully longer than the confirm chain already masked it.
 */
export const PIN_CONFIRM_RECONCILE_GRACE_MS = 10_000

/**
 * Open a pin/unpin operation on `key`: bumps the epoch (superseding any older
 * operation on the same key) and opens the reconcile window. Returns the new
 * epoch, which the caller hands back to `endPinOperation`.
 */
export function beginPinOperation(
    key: string,
    post: Post,
    next: boolean,
    initiatingDid: string | undefined
): number {
    clearGraceTimer(key)
    const epoch = (epochByKey.get(key) ?? 0) + 1
    epochByKey.set(key, epoch)
    pendingByKey.set(key, { post, next, epoch, initiatingDid })
    return epoch
}

/** The newest epoch opened for `key`, or undefined if none ever was. */
export function currentPinEpoch(key: string): number | undefined {
    return epochByKey.get(key)
}

/**
 * Close the reconcile window for `key`, but only while it is still owned by
 * `epoch` — a newer operation's window must not be closed by an older one
 * reaching its terminal state afterwards.
 */
export function endPinOperation(key: string, epoch: number | undefined): void {
    if (epoch === undefined) return
    if (pendingByKey.get(key)?.epoch === epoch) {
        clearGraceTimer(key)
        pendingByKey.delete(key)
    }
}

/**
 * Close the window `delayMs` from now instead of immediately — the confirmed
 * case only (see `PIN_CONFIRM_RECONCILE_GRACE_MS`).
 *
 * The deferred close goes through `endPinOperation`, so a newer operation that
 * takes the key during the grace is not torn down by it.
 */
export function scheduleEndPinOperation(
    key: string,
    epoch: number | undefined,
    delayMs: number
): void {
    if (epoch === undefined) return
    if (pendingByKey.get(key)?.epoch !== epoch) return
    clearGraceTimer(key)
    graceTimerByKey.set(
        key,
        setTimeout(() => {
            graceTimerByKey.delete(key)
            endPinOperation(key, epoch)
        }, delayMs)
    )
}

/** The operation currently holding `key`'s reconcile window, if any. */
export function getPendingPinOperation(key: string): PendingPinOperation | undefined {
    return pendingByKey.get(key)
}

interface PostsInfiniteData {
    pages: PostsPage[]
    pageParams: unknown[]
}

/**
 * `structuralSharing` hands us `unknown`, and it runs for every result written
 * to the query — including `setQueryData` calls that could carry something
 * else entirely. Only reconcile what is actually a posts infinite-query payload.
 */
function isPostsInfiniteData(value: unknown): value is PostsInfiniteData {
    if (typeof value !== 'object' || value === null) return false
    const pages = (value as { pages?: unknown }).pages
    return (
        Array.isArray(pages) &&
        pages.every(
            page =>
                typeof page === 'object' &&
                page !== null &&
                Array.isArray((page as { posts?: unknown }).posts)
        )
    )
}

/**
 * Merge a freshly-fetched (or otherwise written) page set with the pin/unpin
 * the user has already performed but the server has not caught up with.
 *
 * A no-op when no operation is pending for `key` — the overwhelmingly common
 * case, so ordinary reads pay one Map lookup and nothing else.
 *
 * Also a no-op when `activeDid` is not the account the operation was opened
 * under. The key does not identify the account (see `initiatingDid`), so an
 * account switch mid-window would otherwise replay the previous account's
 * pending pin over the new account's view of that same profile.
 *
 * `applyOptimisticPin` is idempotent: re-applying a pin to data that already
 * shows it pinned produces the same rows in the same order. That is what makes
 * this safe to run over a page that already agrees, and over the same data more
 * than once (e.g. the optimistic write, then a `fetchNextPage` result).
 */
export function reconcilePendingPin(
    key: string,
    data: unknown,
    activeDid: string | undefined
): unknown {
    const pending = pendingByKey.get(key)
    if (!pending) return data
    if (pending.initiatingDid !== activeDid) return data
    if (!isPostsInfiniteData(data)) return data
    return { ...data, pages: applyOptimisticPin(data.pages, pending.post, pending.next) }
}

/** Test-only: drop all cross-mount pin state so cases cannot leak into each other. */
export function resetPinnedSlotSyncForTests(): void {
    for (const timer of graceTimerByKey.values()) clearTimeout(timer)
    graceTimerByKey.clear()
    epochByKey.clear()
    pendingByKey.clear()
}
